import GroupChatIconSelector from './GroupChatIconSelector';
import { type GroupChatIconChoice } from './GroupChatIconPresetPicker';
import React, { useEffect, useMemo, useState } from 'react';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import { PersonAvatar } from '../../common/avatar/PersonAvatar';
import { MemberRoleBadges } from '../../common/chat/members/MemberListSectionParts';
import '../../../css/admin/chats/EventDerivedGroupModal.css';

const TypedPersonAvatar = PersonAvatar as React.ComponentType<any>;

type EventChatArtist = {
  key?: string;
  name?: string;
  partLabel?: string;
  eventPartLabel?: string;
  mainPartLabel?: string;
  allPerformancesGroupLabel?: string;
  selectionPartLabel?: string;
  avatarUrl?: string;
  pic?: string;
  gender?: string;
  initial?: string;
  isChief?: boolean;
  isConcertmaster?: boolean;
  customRoles?: Array<{ id?: string; name?: string; role_name?: string }>;
  payload?: { artist_id?: string; id?: string; artist_name?: string; name?: string };
  artist_id?: string;
  id?: string;
};

type EventDerivedGroupModalProps = {
  open: boolean;
  eventName?: string;
  artists?: EventChatArtist[];
  saving?: boolean;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  onClose: () => void;
  onCreate?: (input: { title: string; artistIds: string[]; iconFile: string }) => void | Promise<void>;
};

const artistIdOf = (artist: EventChatArtist) => String(
  artist?.payload?.artist_id || artist?.payload?.id || artist?.artist_id || artist?.id || ''
).trim();

const artistPartOf = (artist: EventChatArtist) => String(
  artist?.selectionPartLabel
  || artist?.partLabel
  || artist?.mainPartLabel
  || artist?.allPerformancesGroupLabel
  || artist?.eventPartLabel
  || 'パート未設定'
).trim() || 'パート未設定';

type SelectionGroup = { key: string; label: string; artistIds: string[] };

const appendToSelectionGroup = (
  groups: Map<string, SelectionGroup>,
  key: string,
  label: string,
  artistId: string,
) => {
  if (!artistId) return;
  if (!groups.has(key)) groups.set(key, { key, label, artistIds: [] });
  const group = groups.get(key);
  if (group && !group.artistIds.includes(artistId)) group.artistIds.push(artistId);
};

export default function EventDerivedGroupModal({
  open,
  eventName,
  artists = [],
  saving = false,
  loading = false,
  error = '',
  onRetry,
  onClose,
  onCreate,
}: EventDerivedGroupModalProps) {
  const [step, setStep] = useState('members');
  const [keyword, setKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [title, setTitle] = useState('');
  const [iconChoice, setIconChoice] = useState<GroupChatIconChoice | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('members');
    setKeyword('');
    setSelectedIds(new Set());
    setTitle(''); setIconChoice(null);
  }, [open]);

  const candidates = useMemo(() => {
    const seen = new Set<string>();
    return artists.filter((artist) => {
      const id = artistIdOf(artist);
      if (!id || seen.has(id)) return false;
      seen.add(id); return true;
    });
  }, [artists]);
  const selectedArtistIds = candidates.map(artistIdOf).filter((id) => selectedIds.has(id));
  useEffect(() => {
    setSelectedIds((current) => {
      const next = new Set(candidates.map(artistIdOf).filter((id) => current.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [candidates]);

  const visibleArtists = useMemo(() => {
    const query = keyword.trim().toLocaleLowerCase('ja');
    if (!query) return candidates;
    return candidates.filter((artist) => [
      artist?.name,
      artistPartOf(artist),
      artist?.payload?.artist_name,
      artist?.payload?.name,
    ].some((value) => String(value || '').toLocaleLowerCase('ja').includes(query)));
  }, [candidates, keyword]);

  const selectedArtists = useMemo(
    () => candidates.filter((artist) => selectedIds.has(artistIdOf(artist))),
    [candidates, selectedIds]
  );

  const partSelectionGroups = useMemo(() => {
    const groups = new Map<string, SelectionGroup>();
    candidates.forEach((artist) => {
      const label = artistPartOf(artist);
      appendToSelectionGroup(groups, `part:${label}`, label, artistIdOf(artist));
    });
    return Array.from(groups.values());
  }, [candidates]);

  const roleSelectionGroups = useMemo(() => {
    const groups = new Map<string, SelectionGroup>([
      ['role:concertmaster', { key: 'role:concertmaster', label: 'コンサートマスター', artistIds: [] }],
      ['role:chief', { key: 'role:chief', label: '首席', artistIds: [] }],
    ]);
    candidates.forEach((artist) => {
      const artistId = artistIdOf(artist);
      if (artist?.isConcertmaster) {
        appendToSelectionGroup(groups, 'role:concertmaster', 'コンサートマスター', artistId);
      }
      if (artist?.isChief) appendToSelectionGroup(groups, 'role:chief', '首席', artistId);
      (Array.isArray(artist?.customRoles) ? artist.customRoles : []).forEach((role) => {
        const label = String(role?.name || role?.role_name || '役職').trim() || '役職';
        const key = String(role?.id || label).trim();
        appendToSelectionGroup(groups, `role:${key}`, label, artistId);
      });
    });
    return Array.from(groups.values()).filter((group) => group.artistIds.length > 0);
  }, [candidates]);

  const toggleArtist = (artist: EventChatArtist) => {
    const artistId = artistIdOf(artist);
    if (!artistId) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(artistId)) next.delete(artistId);
      else next.add(artistId);
      return next;
    });
  };

  const toggleSelectionGroup = (artistIds: string[]) => {
    const normalizedIds = Array.from(new Set(artistIds.filter(Boolean)));
    if (normalizedIds.length === 0) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      const allSelected = normalizedIds.every((artistId) => next.has(artistId));
      normalizedIds.forEach((artistId) => {
        if (allSelected) next.delete(artistId);
        else next.add(artistId);
      });
      return next;
    });
  };

  const renderSelectionGroups = (label: string, groups: SelectionGroup[]) => {
    if (groups.length === 0) return null;
    return (
      <section className="event-derived-group-modal__bulk-section" aria-label={label}>
        <h4>{label}</h4>
        <div className="event-derived-group-modal__bulk-options">
          {groups.map((group) => {
            const selected = group.artistIds.every((artistId) => selectedIds.has(artistId));
            return (
              <DsButton
                key={group.key}
                type="button"
                variant="secondary"
                size="sm"
                className={selected ? 'is-selected' : ''}
                onClick={() => toggleSelectionGroup(group.artistIds)}
                aria-pressed={selected}
              >
                <span>{group.label}</span>
                <small>{group.artistIds.length}人</small>
              </DsButton>
            );
          })}
        </div>
      </section>
    );
  };

  const submit = async () => {
    const normalizedTitle = title.trim();
    if (saving || loading || !normalizedTitle || normalizedTitle.length > 80 || selectedArtistIds.length === 0 || !iconChoice) return;
    await onCreate?.({
      title: normalizedTitle,
      artistIds: selectedArtistIds, iconFile: iconChoice.iconFile,
    });
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      dismissible={!saving}
      title={step === 'members' ? 'グループチャットメンバーを選択' : 'グループ名を決める'}
      ariaLabel={step === 'members' ? 'グループチャットメンバーを選択' : 'グループ名を決める'}
      size="md"
      className="event-derived-group-modal"
      footer={step === 'members' ? (
        <>
          <DsButton
            type="button"
            onClick={() => setStep('name')}
            disabled={selectedArtistIds.length === 0 || loading || Boolean(error)}
          >
            次へ
          </DsButton>
        </>
      ) : (
        <>
          <DsButton type="button" variant="secondary" onClick={() => setStep('members')} disabled={saving}>
            戻る
          </DsButton>
          <DsButton type="button" onClick={submit} loading={saving} disabled={!title.trim() || title.trim().length > 80 || !iconChoice || saving || selectedArtistIds.length === 0}>
            グループを作成
          </DsButton>
        </>
      )}
    >
      {error ? <p role="alert">{error}</p> : null}
      {step === 'members' && error && onRetry ? <DsButton variant="secondary" disabled={loading} onClick={onRetry}>再読み込み</DsButton> : null}
      {loading ? <p role="status">メンバーを読み込んでいます…</p> : null}
      {step === 'members' ? (
        <div className="event-derived-group-modal__members">
          <div className="event-derived-group-modal__summary">
            <span>{eventName || 'イベント名未設定'}</span>
            <span>選択 {selectedArtistIds.length}人</span>
          </div>
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="名前・パートで絞り込み"
            aria-label="イベントチャットメンバーを絞り込み"
          />
          {!loading && !error ? <div className="event-derived-group-modal__bulk-select">
            {renderSelectionGroups('パートで選択', partSelectionGroups)}
            {renderSelectionGroups('バッジで選択', roleSelectionGroups)}
          </div> : null}
          {!loading && !error ? <div className="event-derived-group-modal__member-list">
            {visibleArtists.length > 0 ? visibleArtists.map((artist) => {
              const artistId = artistIdOf(artist);
              const selected = selectedIds.has(artistId);
              return (
                <DsButton
                  key={artistId || artist?.key}
                  type="button"
                  variant="secondary"
                  className={`event-derived-group-modal__memberRow${selected ? ' is-selected' : ''}`}
                  onClick={() => toggleArtist(artist)}
                  aria-pressed={selected}
                >
                  <TypedPersonAvatar
                    role="artist"
                    name={artist?.name || '名称未設定'}
                    gender={artist?.gender}
                    profilePicture={artist?.pic}
                    imageUrl={artist?.avatarUrl}
                    size={40}
                    className="event-derived-group-modal__avatar"
                  />
                  <span className="event-derived-group-modal__identity">
                    <span className="event-derived-group-modal__name-line">
                      <strong>{artist?.name || '名称未設定'}</strong>
                      <MemberRoleBadges
                        member={artist}
                        badgesClassName="event-derived-group-modal__badges"
                        concertmasterBadgeClassName="event-derived-group-modal__badge event-derived-group-modal__badge--concertmaster"
                        chiefBadgeClassName="event-derived-group-modal__badge event-derived-group-modal__badge--chief"
                        roleBadgeClassName="event-derived-group-modal__badge event-derived-group-modal__badge--role"
                      />
                    </span>
                    <small>{artistPartOf(artist)}</small>
                  </span>
                  <span className="event-derived-group-modal__selectionMark" aria-hidden="true">
                    {selected ? <svg viewBox="0 0 16 16" width="14" height="14" focusable="false"><path d="m3 8 3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> : null}
                  </span>
                </DsButton>
              );
            }) : (
              <p className="event-derived-group-modal__empty">{keyword.trim() ? '該当するメンバーがいません。' : '選択できるイベントチャットメンバーがいません。'}</p>
            )}
          </div> : null}
        </div>
      ) : (
        <div className="event-derived-group-modal__name-step">
          <GroupChatIconSelector value={iconChoice} disabled={saving} buttonLabel={iconChoice ? 'アイコンを変更' : 'アイコンを選択'} onApply={async(choice)=>{setIconChoice(choice);return true;}} />
          <label htmlFor="event-derived-group-title">グループ名</label>
          <input
            id="event-derived-group-title"
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={80}
            disabled={saving}
            autoFocus
            placeholder="グループ名を入力"
          />
          <p className="event-derived-group-modal__linked-event">
            連動イベント: {eventName || 'イベント名未設定'}
          </p>
          <div className="event-derived-group-modal__selected-members">
            {selectedArtists.map((artist) => (
              <span key={artistIdOf(artist)}>{artist?.name || '名称未設定'}</span>
            ))}
          </div>
        </div>
      )}
    </DsModal>
  );
}
